import { cookieService } from "./cookieService";
import { getApiUrl } from "./api";

export interface AnalyticsSession {
  sessionId: string;
  offerId: string;
  publicToken: string;
  isActive: boolean;
  isOwnerView?: boolean;
}

export interface AnalyticsEvent {
  eventType:
    | "view"
    | "click"
    | "scroll"
    | "copy"
    | "print"
    | "hover"
    | "form_interact"
    | "section_enter"
    | "section_exit";
  eventTarget?: string;
  eventData?: any;
  pageSection?: string;
  scrollDepth?: number;
  positionX?: number;
  positionY?: number;
}

interface DeviceInfo {
  deviceType: "desktop" | "mobile" | "tablet";
  browserName: string;
  browserVersion: string;
  operatingSystem: string;
  screenResolution: string;
  viewportSize: string;
  userAgent: string;
  referrer: string;
}

class AnalyticsService {
  private session: AnalyticsSession | null = null;
  private eventBatch: AnalyticsEvent[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly BATCH_TIMEOUT = 5000; // 5 seconds
  private readonly SECTION_UPDATE_INTERVAL = 5000; // 5 seconds - replaces heartbeat
  private sectionUpdateInterval: NodeJS.Timeout | null = null;
  private scrollThrottle: NodeJS.Timeout | null = null;
  private lastScrollDepth = 0;

  // Advanced tracking state
  private isTabVisible = true;
  private isWindowFocused = true;
  private isUserActive = true;
  private currentSection: string | null = null;
  private sectionStartTime: number | null = null;
  private accumulatedTimes: Map<string, number> = new Map();
  private lastActivityTime = Date.now();
  private readonly IDLE_TIMEOUT = 30000; // 30 seconds of inactivity
  private idleCheckInterval: NodeJS.Timeout | null = null;

  // Intersection Observer
  private sectionObserver: IntersectionObserver | null = null;
  private observedSections: Set<Element> = new Set();

  /**
   * Initialize analytics session for an offer
   */
  async startSession(
    publicToken: string,
    viewedOfferId?: string
  ): Promise<boolean> {
    try {
      // Use the same session ID as cookie consent
      const sessionId = cookieService.getSessionId();

      // Get device information
      const deviceInfo = this.getDeviceInfo();

      // Get current consent status
      const hasAnalyticsConsent = cookieService.hasAnalyticsConsent();
      const storageType = cookieService.getStorageType();

      // Get JWT token if user is logged in
      const viewerToken = localStorage.getItem("token");

      // Start session via API
      const response = await fetch(
        `${getApiUrl()}/offers/public/${publicToken}/analytics/session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId,
            analyticsConsent: hasAnalyticsConsent,
            storageType,
            viewerToken,
            viewedOfferId, // Pass the specific offer version ID being viewed
            ...deviceInfo,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        this.session = {
          sessionId,
          offerId: "",
          publicToken,
          isActive: true,
          isOwnerView: result.isOwnerView,
        };

        // Start tracking events only if not owner view
        if (!result.isOwnerView) {
          this.setupEventListeners();
          this.setupIntersectionObserver();
          this.startSectionTracking();
        }

        const viewerMessage = result.isOwnerView
          ? " (owner view - analytics disabled)"
          : "";

        console.log(
          `Analytics session started: ${sessionId} (storage: ${storageType})${viewerMessage}`
        );
        return true;
      } else {
        console.log("Failed to start analytics session:", result.message);
        return false;
      }
    } catch (error) {
      console.error("Error starting analytics session:", error);
      return false;
    }
  }

  /**
   * Track analytics event
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.session?.isActive || this.session?.isOwnerView) {
      return;
    }

    this.eventBatch.push({
      ...event,
      eventTarget: event.eventTarget || document.title,
    });

    // Send batch if it reaches the size limit
    if (this.eventBatch.length >= this.BATCH_SIZE) {
      this.sendEventBatch();
    } else {
      // Set timeout to send batch
      this.scheduleBatchSend();
    }
  }

  /**
   * Track click event with position
   */
  trackClick(target: string, x: number, y: number, section?: string): void {
    this.trackEvent({
      eventType: "click",
      eventTarget: target,
      positionX: x,
      positionY: y,
      pageSection: section,
    });
  }

  /**
   * Track scroll event
   */
  trackScroll(scrollDepth: number): void {
    this.trackEvent({
      eventType: "scroll",
      scrollDepth,
      eventTarget: "page",
    });
  }

  /**
   * Track copy event
   */
  trackCopy(selectedText: string, section?: string): void {
    this.trackEvent({
      eventType: "copy",
      eventTarget: "text-selection",
      eventData: { selectedText: selectedText.substring(0, 500) }, // Limit text length
      pageSection: section,
    });
  }

  /**
   * Track print event
   */
  trackPrint(): void {
    this.trackEvent({
      eventType: "print",
      eventTarget: "page",
    });
  }

  /**
   * Update consent preferences and restart session with new storage type
   */
  async updateConsent(analyticsConsent: boolean): Promise<void> {
    if (!this.session) return;

    try {
      const storageType = cookieService.getStorageType();

      await fetch(
        `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/session/${this.session.sessionId}/consent`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            analyticsConsent,
            storageType,
          }),
        }
      );

      console.log(
        `Analytics consent updated: ${analyticsConsent} (storage: ${storageType})`
      );
    } catch (error) {
      console.error("Error updating analytics consent:", error);
    }
  }

  /**
   * End analytics session
   */
  async endSession(): Promise<void> {
    console.log(
      "[AnalyticsService] endSession() called, session:",
      this.session?.sessionId
    );

    if (!this.session) {
      console.log("[AnalyticsService] No active session to end");
      return;
    }

    // Store session info before clearing (to prevent race conditions)
    const sessionToEnd = { ...this.session };

    // IMPORTANT: Clear session state immediately so isSessionActive() returns false
    // This prevents new operations from starting while we're cleaning up
    this.session = null;
    console.log(
      "[AnalyticsService] Session state cleared, starting cleanup..."
    );

    try {
      // Send remaining events
      if (this.eventBatch.length > 0) {
        console.log(
          `[AnalyticsService] Sending ${this.eventBatch.length} remaining events...`
        );
        await this.sendEventBatch();
      }

      // Try to end session via regular fetch first
      try {
        console.log("[AnalyticsService] Calling end session endpoint...");
        await fetch(
          `${getApiUrl()}/offers/public/${sessionToEnd.publicToken}/analytics/session/${sessionToEnd.sessionId}/end`,
          {
            method: "POST",
          }
        );
        console.log(
          "[AnalyticsService] End session endpoint called successfully"
        );
      } catch (error) {
        console.warn(
          "[AnalyticsService] Regular fetch failed, trying Beacon API...",
          error
        );
        // If regular fetch fails, try Beacon API as fallback
        if (navigator.sendBeacon) {
          const endSessionUrl = `${getApiUrl()}/offers/public/${sessionToEnd.publicToken}/analytics/session/${sessionToEnd.sessionId}/end`;
          navigator.sendBeacon(
            endSessionUrl,
            JSON.stringify({ timestamp: Date.now() })
          );
          console.log("[AnalyticsService] Beacon API sent");
        }
      }

      // Cleanup
      console.log("[AnalyticsService] Cleaning up listeners and observers...");
      this.cleanupEventListeners();
      this.cleanupIntersectionObserver();
      this.stopSectionTracking();
      this.eventBatch = [];
      this.accumulatedTimes.clear();

      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
        this.batchTimeout = null;
      }

      console.log("[AnalyticsService] Session ended successfully");
    } catch (error) {
      console.error(
        "[AnalyticsService] Error ending analytics session:",
        error
      );
      throw error; // Re-throw so caller knows there was an error
    }
  }

  /**
   * Check if session is active
   */
  isSessionActive(): boolean {
    return this.session?.isActive || false;
  }

  /**
   * Setup Intersection Observer for automatic section detection
   */
  private setupIntersectionObserver(): void {
    if (!window.IntersectionObserver) {
      console.warn(
        "IntersectionObserver not supported, falling back to manual tracking"
      );
      return;
    }

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const section = entry.target.getAttribute("data-section");
          if (!section) return;

          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // Section is primarily visible
            this.handleSectionVisible(section);
          } else if (this.currentSection === section) {
            // Section is no longer primarily visible
            this.handleSectionHidden(section);
          }
        });
      },
      {
        threshold: [0.5],
        rootMargin: "-10% 0px",
      }
    );

    // Observe all existing sections
    this.observeExistingSections();
  }

  /**
   * Observe existing sections on the page
   */
  private observeExistingSections(): void {
    if (!this.sectionObserver) return;

    const sections = document.querySelectorAll("[data-section]");
    console.log(
      `🔍 [Analytics Debug] Found ${sections.length} sections with data-section attribute:`,
      Array.from(sections).map((s) => s.getAttribute("data-section"))
    );

    if (sections.length === 0) {
      console.warn(
        "⚠️ [Analytics Debug] No sections found with data-section attribute! Automatic tracking will not work."
      );
      console.log(
        '💡 [Analytics Debug] To fix: Add data-section="section-name" to trackable elements'
      );
    }

    sections.forEach((section) => {
      this.sectionObserver!.observe(section);
      this.observedSections.add(section);
      console.log(
        `👁️ [Analytics Debug] Now observing section: ${section.getAttribute("data-section")}`
      );
    });
  }

  /**
   * Start section-based tracking to replace heartbeat system
   */
  private startSectionTracking(): void {
    if (this.sectionUpdateInterval) {
      clearInterval(this.sectionUpdateInterval);
    }

    console.log("🚀 [Analytics Debug] Starting section tracking...");

    // Setup activity detection
    this.setupActivityDetection();

    this.sectionUpdateInterval = setInterval(() => {
      console.log("⏰ [Analytics Debug] Section tracking interval triggered");
      this.processSectionTimeUpdate();
    }, this.SECTION_UPDATE_INTERVAL);

    console.log(
      `✅ [Analytics Debug] Section tracking started with ${this.SECTION_UPDATE_INTERVAL}ms interval`
    );
  }

  /**
   * Stop section tracking
   */
  private stopSectionTracking(): void {
    if (this.sectionUpdateInterval) {
      clearInterval(this.sectionUpdateInterval);
      this.sectionUpdateInterval = null;
    }

    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }
  }

  /**
   * Setup activity detection for user engagement
   */
  private setupActivityDetection(): void {
    // Page Visibility API
    const handleVisibilityChange = () => {
      this.isTabVisible = !document.hidden;
      if (!this.isTabVisible && this.currentSection) {
        this.pauseSectionTracking();
      } else if (this.isTabVisible && this.currentSection) {
        this.resumeSectionTracking();
      }
    };

    // Window Focus/Blur
    const handleFocus = () => {
      this.isWindowFocused = true;
      if (this.currentSection) {
        this.resumeSectionTracking();
      }
    };

    const handleBlur = () => {
      this.isWindowFocused = false;
      if (this.currentSection) {
        this.pauseSectionTracking();
      }
    };

    // User activity detection
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    const handleActivity = () => {
      this.lastActivityTime = Date.now();
      if (!this.isUserActive) {
        this.isUserActive = true;
        if (this.currentSection) {
          this.resumeSectionTracking();
        }
      }
    };

    // Add activity listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Setup idle detection
    this.idleCheckInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - this.lastActivityTime;
      if (timeSinceActivity > this.IDLE_TIMEOUT && this.isUserActive) {
        this.isUserActive = false;
        if (this.currentSection) {
          this.pauseSectionTracking();
        }
      }
    }, 5000);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    // Store for cleanup
    this.activityListeners = {
      visibilitychange: handleVisibilityChange,
      focus: handleFocus,
      blur: handleBlur,
      activity: activityEvents.map((event) => ({
        event,
        handler: handleActivity,
      })),
    };
  }

  /**
   * Handle section becoming visible
   */
  private handleSectionVisible(sectionName: string): void {
    if (this.currentSection === sectionName) return;

    console.log(`👀 [Analytics Debug] Section became visible: ${sectionName}`);

    // End tracking for previous section
    if (this.currentSection) {
      console.log(
        `🔄 [Analytics Debug] Switching from ${this.currentSection} to ${sectionName}`
      );
      this.handleSectionHidden(this.currentSection);
    }

    // Start tracking new section
    this.currentSection = sectionName;
    if (this.shouldTrackTime()) {
      this.sectionStartTime = Date.now();
      console.log(
        `⏱️ [Analytics Debug] Started timing for section: ${sectionName}`
      );
    } else {
      console.log(
        `⏸️ [Analytics Debug] Section ${sectionName} visible but tracking paused (tab=${this.isTabVisible}, focus=${this.isWindowFocused}, active=${this.isUserActive})`
      );
    }
  }

  /**
   * Handle section becoming hidden
   */
  private handleSectionHidden(sectionName: string): void {
    if (this.currentSection !== sectionName || !this.sectionStartTime) return;

    // Calculate time spent
    const timeSpent = Date.now() - this.sectionStartTime;
    console.log(
      `🚪 [Analytics Debug] Section ${sectionName} became hidden, time spent: ${timeSpent}ms`
    );

    // Add to accumulated time
    const currentAccumulated = this.accumulatedTimes.get(sectionName) || 0;
    this.accumulatedTimes.set(sectionName, currentAccumulated + timeSpent);
    console.log(
      `📊 [Analytics Debug] Total accumulated time for ${sectionName}: ${currentAccumulated + timeSpent}ms`
    );

    // Reset tracking
    this.currentSection = null;
    this.sectionStartTime = null;
  }

  /**
   * Pause section tracking when conditions aren't met
   */
  private pauseSectionTracking(): void {
    if (this.currentSection && this.sectionStartTime) {
      const timeSpent = Date.now() - this.sectionStartTime;
      const currentAccumulated =
        this.accumulatedTimes.get(this.currentSection) || 0;
      this.accumulatedTimes.set(
        this.currentSection,
        currentAccumulated + timeSpent
      );
      this.sectionStartTime = null;
    }
  }

  /**
   * Resume section tracking when conditions are met
   */
  private resumeSectionTracking(): void {
    if (
      this.currentSection &&
      !this.sectionStartTime &&
      this.shouldTrackTime()
    ) {
      this.sectionStartTime = Date.now();
    }
  }

  /**
   * Check if we should track time based on all conditions
   */
  private shouldTrackTime(): boolean {
    return this.isTabVisible && this.isWindowFocused && this.isUserActive;
  }

  /**
   * Process section time updates (replaces heartbeat)
   */
  private processSectionTimeUpdate(): void {
    console.log("🔄 [Analytics Debug] Processing section time update...");

    if (!this.session?.isActive) {
      console.log("❌ [Analytics Debug] Session not active, skipping update");
      return;
    }

    if (this.session?.isOwnerView) {
      console.log("👤 [Analytics Debug] Owner view, skipping analytics");
      return;
    }

    console.log(`📍 [Analytics Debug] Current section: ${this.currentSection}`);
    console.log(
      `⏱️ [Analytics Debug] Section start time: ${this.sectionStartTime}`
    );
    console.log(
      `🎯 [Analytics Debug] Should track time: ${this.shouldTrackTime()}`
    );
    console.log(
      `📈 [Analytics Debug] Accumulated times:`,
      Object.fromEntries(this.accumulatedTimes)
    );

    // Update current section time if actively tracking
    if (
      this.currentSection &&
      this.sectionStartTime &&
      this.shouldTrackTime()
    ) {
      const currentTime = Date.now();
      const additionalTime = currentTime - this.sectionStartTime;
      const currentAccumulated =
        this.accumulatedTimes.get(this.currentSection) || 0;
      this.accumulatedTimes.set(
        this.currentSection,
        currentAccumulated + additionalTime
      );
      console.log(
        `➕ [Analytics Debug] Added ${additionalTime}ms to ${this.currentSection}, total: ${currentAccumulated + additionalTime}ms`
      );
      this.sectionStartTime = currentTime; // Reset start time for next interval
    }

    // Send accumulated times for all sections
    this.sendAccumulatedSectionTimes();
  }

  /**
   * Send accumulated section times to backend
   */
  private async sendAccumulatedSectionTimes(): Promise<void> {
    console.log("📤 [Analytics Debug] Attempting to send section times...");

    if (!this.session) {
      console.log("❌ [Analytics Debug] No session, cannot send section times");
      return;
    }

    if (this.accumulatedTimes.size === 0) {
      console.log("📭 [Analytics Debug] No accumulated times to send");
      return;
    }

    try {
      const sectionUpdates = Array.from(this.accumulatedTimes.entries())
        .filter(([sectionName, timeSpentMs]) => {
          // Filter out sections with 0 or negative time
          const isValid =
            timeSpentMs > 0 && !isNaN(timeSpentMs) && isFinite(timeSpentMs);
          if (!isValid) {
            console.log(
              `🚫 [Analytics Debug] Filtering out section ${sectionName} with invalid time: ${timeSpentMs}ms`
            );
          }
          return isValid;
        })
        .map(([sectionName, timeSpentMs]) => ({
          sectionName,
          timeSpentMs,
          interactionCount: 0, // Interaction count handled separately
        }));

      console.log(
        "📊 [Analytics Debug] Sending section updates (after filtering):",
        sectionUpdates
      );

      if (sectionUpdates.length === 0) {
        console.log(
          "📭 [Analytics Debug] No valid section updates to send after filtering"
        );
        return;
      }

      const url = `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/session/${this.session.sessionId}/section-time`;
      console.log("🌐 [Analytics Debug] Sending to URL:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sections: sectionUpdates,
        }),
      });

      if (response.ok) {
        console.log("✅ [Analytics Debug] Section times sent successfully");
        // Clear accumulated times after successful send
        this.accumulatedTimes.clear();
        console.log("🧹 [Analytics Debug] Cleared accumulated times");
      } else {
        console.error(
          "❌ [Analytics Debug] Failed to send section times:",
          response.status,
          response.statusText
        );
      }
    } catch (error) {
      console.error(
        "❌ [Analytics Debug] Error sending section time updates:",
        error
      );
    }
  }

  /**
   * Cleanup intersection observer
   */
  private cleanupIntersectionObserver(): void {
    if (this.sectionObserver) {
      this.observedSections.forEach((element) => {
        this.sectionObserver!.unobserve(element);
      });
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
      this.observedSections.clear();
    }
  }

  /**
   * Register a section for automatic tracking
   */
  registerSection(element: Element): void {
    if (!this.sectionObserver || this.observedSections.has(element)) return;

    this.sectionObserver.observe(element);
    this.observedSections.add(element);
  }

  /**
   * Unregister a section from tracking
   */
  unregisterSection(element: Element): void {
    if (!this.sectionObserver || !this.observedSections.has(element)) return;

    this.sectionObserver.unobserve(element);
    this.observedSections.delete(element);
  }

  private activityListeners: any = {};

  /**
   * Send event batch to backend
   */
  private async sendEventBatch(): Promise<void> {
    if (!this.session || this.eventBatch.length === 0) return;

    try {
      const events = [...this.eventBatch];
      this.eventBatch = [];

      const response = await fetch(
        `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/events`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId: this.session.sessionId,
            events,
          }),
        }
      );

      if (!response.ok) {
        console.error("Failed to send analytics events");
        // Re-add events to batch for retry (optional)
        this.eventBatch.unshift(...events);
      }
    } catch (error) {
      console.error("Error sending analytics events:", error);
    }
  }

  /**
   * Send section time data
   */
  private async sendSectionTime(
    sectionName: string,
    timeSpentMs: number,
    interactionCount: number
  ): Promise<void> {
    if (!this.session) return;

    try {
      await fetch(
        `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/session/${this.session.sessionId}/section-time`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sectionName,
            timeSpentMs,
            interactionCount,
          }),
        }
      );
    } catch (error) {
      console.error("Error sending section time:", error);
    }
  }

  /**
   * Schedule batch send
   */
  private scheduleBatchSend(): void {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.sendEventBatch();
    }, this.BATCH_TIMEOUT);
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `analytics_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get device information
   */
  private getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;

    // Detect device type
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
    const isTablet =
      /iPad|Android(?=.*\bMobile\b)(?=.*\b(?:Phone|Tablet)\b)/i.test(userAgent);

    let deviceType: "desktop" | "mobile" | "tablet" = "desktop";
    if (isTablet) deviceType = "tablet";
    else if (isMobile) deviceType = "mobile";

    // Detect browser
    let browserName = "Unknown";
    let browserVersion = "Unknown";

    if (userAgent.includes("Chrome")) {
      browserName = "Chrome";
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    } else if (userAgent.includes("Firefox")) {
      browserName = "Firefox";
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    } else if (userAgent.includes("Safari")) {
      browserName = "Safari";
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : "Unknown";
    }

    // Detect OS
    let operatingSystem = "Unknown";
    if (userAgent.includes("Windows")) operatingSystem = "Windows";
    else if (userAgent.includes("Mac")) operatingSystem = "macOS";
    else if (userAgent.includes("Linux")) operatingSystem = "Linux";
    else if (userAgent.includes("Android")) operatingSystem = "Android";
    else if (userAgent.includes("iOS")) operatingSystem = "iOS";

    return {
      deviceType,
      browserName,
      browserVersion,
      operatingSystem,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      userAgent,
      referrer: document.referrer || "direct",
    };
  }

  /**
   * Setup event listeners for automatic tracking
   */
  private setupEventListeners(): void {
    // Track scroll with throttling
    const handleScroll = () => {
      if (this.scrollThrottle) return;

      this.scrollThrottle = setTimeout(() => {
        const scrollTop = window.pageYOffset;
        const documentHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const scrollDepth = Math.round((scrollTop / documentHeight) * 100);

        // Only track if scroll depth changed by at least 10%
        if (Math.abs(scrollDepth - this.lastScrollDepth) >= 10) {
          this.trackScroll(scrollDepth);
          this.lastScrollDepth = scrollDepth;
        }

        this.scrollThrottle = null;
      }, 250);
    };

    // Track text selection/copy
    const handleCopy = () => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        this.trackCopy(selection.toString());
      }
    };

    // Track print intent
    const handleBeforePrint = () => {
      this.trackPrint();
    };

    // Track page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden && this.session?.isActive) {
        // Send any pending events before page becomes hidden
        if (this.eventBatch.length > 0) {
          this.sendEventBatch();
        }
      }
    };

    // Handle page unload with Beacon API
    const handleBeforeUnload = () => {
      if (this.session?.isActive && !this.session?.isOwnerView) {
        // Send pending events via Beacon
        if (this.eventBatch.length > 0 && navigator.sendBeacon) {
          const eventUrl = `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/events`;
          const eventData = JSON.stringify({
            sessionId: this.session.sessionId,
            events: this.eventBatch,
          });
          navigator.sendBeacon(eventUrl, eventData);
        }

        // Try to end session via Beacon
        if (navigator.sendBeacon) {
          const endSessionUrl = `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/session/${this.session.sessionId}/end`;
          navigator.sendBeacon(
            endSessionUrl,
            JSON.stringify({ timestamp: Date.now() })
          );
        }
      }
    };

    // Handle page hide (more reliable than beforeunload)
    const handlePageHide = () => {
      if (this.session?.isActive && !this.session?.isOwnerView) {
        // Similar to beforeunload but more reliable
        if (this.eventBatch.length > 0 && navigator.sendBeacon) {
          const eventUrl = `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/events`;
          const eventData = JSON.stringify({
            sessionId: this.session.sessionId,
            events: this.eventBatch,
          });
          navigator.sendBeacon(eventUrl, eventData);
        }

        // End session via Beacon
        if (navigator.sendBeacon) {
          const endSessionUrl = `${getApiUrl()}/offers/public/${this.session.publicToken}/analytics/session/${this.session.sessionId}/end`;
          navigator.sendBeacon(
            endSessionUrl,
            JSON.stringify({ timestamp: Date.now() })
          );
        }
      }
    };

    // Add event listeners
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("copy", handleCopy);
    window.addEventListener("beforeprint", handleBeforePrint);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    // Store event listeners for cleanup
    this.eventListeners = {
      scroll: handleScroll,
      copy: handleCopy,
      beforeprint: handleBeforePrint,
      visibilitychange: handleVisibilityChange,
      beforeunload: handleBeforeUnload,
      pagehide: handlePageHide,
    };
  }

  private eventListeners: any = {};

  /**
   * Cleanup event listeners
   */
  private cleanupEventListeners(): void {
    if (this.eventListeners.scroll) {
      window.removeEventListener("scroll", this.eventListeners.scroll);
    }
    if (this.eventListeners.copy) {
      document.removeEventListener("copy", this.eventListeners.copy);
    }
    if (this.eventListeners.beforeprint) {
      window.removeEventListener(
        "beforeprint",
        this.eventListeners.beforeprint
      );
    }
    if (this.eventListeners.visibilitychange) {
      document.removeEventListener(
        "visibilitychange",
        this.eventListeners.visibilitychange
      );
    }
    if (this.eventListeners.beforeunload) {
      window.removeEventListener(
        "beforeunload",
        this.eventListeners.beforeunload
      );
    }
    if (this.eventListeners.pagehide) {
      window.removeEventListener("pagehide", this.eventListeners.pagehide);
    }

    this.eventListeners = {};

    if (this.scrollThrottle) {
      clearTimeout(this.scrollThrottle);
      this.scrollThrottle = null;
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
